import { useEffect, useRef, useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import SubPageSection from '@/components/layout/SubPageSection';
import SubPageSurface from '@/components/layout/SubPageSurface';
import { publicHeroFile } from '@/utils/publicHeroFile';
import { SUBPAGE_SURFACE, SUBPAGE_SURFACE_BORDER } from '@/constants/surfaceColors';

const services = [
  {
    icon: 'ri-building-2-line',
    title: '建築設計',
    titleEn: 'Architectural Design',
    desc: '公共施設・福祉施設・物流施設・工場・事務所・店舗など、多様な用途の建築設計を手がけます。',
  },
  {
    icon: 'ri-tools-line',
    title: '構造設計',
    titleEn: 'Structural Engineering',
    desc: '鉄筋コンクリート造・鉄骨造・木造など、各種構造形式に対応した安全で合理的な構造設計を行います。',
  },
  {
    icon: 'ri-file-list-3-line',
    title: '積算業務',
    titleEn: 'Cost Estimation',
    desc: '設計内容に基づく精度の高い積算業務を実施し、適正なコスト管理をサポートします。',
  },
  {
    icon: 'ri-eye-line',
    title: '工事監理',
    titleEn: 'Construction Supervision',
    desc: '設計意図を正確に施工へ反映するため、工事監理業務を誠実に遂行します。',
  },
  {
    icon: 'ri-search-eye-line',
    title: '調査・診断',
    titleEn: 'Inspection & Diagnosis',
    desc: '建築物の法定点検・定期調査報告、耐震診断・耐震設計など、既存建物の安全確保を支援します。',
  },
  {
    icon: 'ri-draft-line',
    title: '企画・計画',
    titleEn: 'Planning & Proposal',
    desc: '建築物の調査・企画から基本計画の策定まで、プロジェクトの初期段階から丁寧に対応します。',
  },
];

export default function ServiceSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <SubPageSection id="service" spacing="wide">
      <div ref={ref}>
        <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <SectionHeading label="SERVICE" title="業務内容" tone="dark" />
        </div>

        <SubPageSurface>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px"
            style={{ background: SUBPAGE_SURFACE_BORDER }}
          >
            {services.map((svc, i) => (
              <ServiceCard key={svc.title} svc={svc} delay={i * 60} visible={visible} />
            ))}
          </div>
        </SubPageSurface>
      </div>
    </SubPageSection>
  );
}

function ServiceCard({
  svc,
  delay,
  visible,
}: {
  svc: (typeof services)[0];
  delay: number;
  visible: boolean;
}) {
  const [hovered, setHovered] = useState(false);

  const metalLayer = `image-set(url("${publicHeroFile('panching_metal.jpg')}") 1x, url("${publicHeroFile('panching_metal@2x.jpg')}") 2x)`;
  const hoverBackground = {
    backgroundImage: `linear-gradient(0deg, rgba(0,0,0,0.7), rgba(0,0,0,0.7)), ${metalLayer}`,
    backgroundSize: 'cover, cover',
    backgroundPosition: 'center, center',
    backgroundRepeat: 'no-repeat, no-repeat',
  } as const;

  return (
    <div
      className={`p-8 md:p-10 cursor-default overflow-hidden transition-all duration-400 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{
        transitionDelay: `${delay}ms`,
        ...(hovered
          ? hoverBackground
          : { background: SUBPAGE_SURFACE }),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Icon */}
      <div className="w-10 h-10 flex items-center justify-center mb-7">
        <i
          className={svc.icon}
          style={{
            fontSize: '22px',
            color: hovered ? 'rgba(255,255,255,0.7)' : '#555555',
            transition: 'color 0.3s',
          }}
        />
      </div>

      {/* Title */}
      <h3
        className="font-bold mb-1 transition-colors duration-300"
        style={{
          fontSize: '18px',
          color: hovered ? '#FFFFFF' : '#111111',
          fontFamily: '"Noto Sans JP", sans-serif',
        }}
      >
        {svc.title}
      </h3>
      <p
        className="mb-5 transition-colors duration-300"
        style={{ fontSize: '14px', color: hovered ? 'rgba(255,255,255,0.92)' : '#9CA3AF', fontFamily: '"Inter", sans-serif', letterSpacing: '0.08em' }}
      >
        {svc.titleEn}
      </p>

      {/* Description */}
      <p
        className="leading-relaxed transition-colors duration-300"
        style={{ fontSize: '15px', lineHeight: '1.9', fontFamily: '"Noto Sans JP", sans-serif', color: hovered ? '#FFFFFF' : '#6B7280' }}
      >
        {svc.desc}
      </p>
    </div>
  );
}
